import { AxiosRequestConfig } from 'axios';
import { axiosInstance } from '@/lib/api.ts';
import { z, ZodType } from 'zod';
import { CONFIG } from '@/config.ts';
import { ENV_CONFIG } from '@/envConfig.ts';
import { handleMock, MockOptions } from '@/common/api/apiMock.ts';
import { ResponseBase } from '@/common/api/schemas.ts';

function parseResponse<TSchema extends ZodType>(
  response: unknown,
  schema: TSchema | undefined,
  method: string,
  url: string
): z.output<TSchema> {
  if (!schema) {
    return response as z.output<TSchema>;
  }

  const result = schema.safeParse(response);
  if (result.success) {
    return result.data;
  } else {
    const formattedFirst10Issues = result.error.issues
      .slice(0, 10)
      .map((issue) => ` - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n\n');

    const validationError = `Response validation errors (first 10):\n\n${formattedFirst10Issues}`;
    const error = `Invalid API response for endpoint ${method} ${ENV_CONFIG.apiBaseUrl}${CONFIG.apiPrefix}${url}`;

    if (import.meta.env.PROD) {
      console.error(error, validationError);
      return response as z.output<TSchema>;
    } else {
      throw new Error(error, { cause: validationError });
    }
  }
}

export const ApiClient = {
  async get<TSchema extends ZodType>(
    url: string,
    responseSchema?: TSchema,
    params?: unknown,
    config?: Omit<AxiosRequestConfig, 'params'> &
      MockOptions<z.input<TSchema> extends ResponseBase ? z.input<TSchema> : never>
  ): Promise<z.output<TSchema>> {
    if (config?.mock && ENV_CONFIG.enableMocks) {
      return parseResponse(await handleMock(config), responseSchema, 'mocked GET', url);
    }

    const response = await axiosInstance.get(url, { params, ...(config ?? {}) });
    return parseResponse(response.data, responseSchema, 'GET', url);
  },

  async post<TSchema extends ZodType>(
    url: string,
    data?: unknown,
    responseSchema?: TSchema,
    config?: Omit<AxiosRequestConfig, 'data'> &
      MockOptions<z.input<TSchema> extends ResponseBase ? z.input<TSchema> : never>
  ): Promise<z.output<TSchema>> {
    if (config?.mock && ENV_CONFIG.enableMocks) {
      return parseResponse(await handleMock(config), responseSchema, 'mocked POST', url);
    }

    const response = await axiosInstance.post(url, data, config);
    return parseResponse(response.data, responseSchema, 'POST', url);
  },

  async put<TSchema extends ZodType>(
    url: string,
    data?: unknown,
    responseSchema?: TSchema,
    config?: Omit<AxiosRequestConfig, 'data'> &
      MockOptions<z.input<TSchema> extends ResponseBase ? z.input<TSchema> : never>
  ): Promise<z.output<TSchema>> {
    if (config?.mock && ENV_CONFIG.enableMocks) {
      return parseResponse(await handleMock(config), responseSchema, 'mocked PUT', url);
    }

    const response = await axiosInstance.put(url, data, config);
    return parseResponse(response.data, responseSchema, 'PUT', url);
  },

  async patch<TSchema extends ZodType>(
    url: string,
    data?: unknown,
    responseSchema?: TSchema,
    config?: Omit<AxiosRequestConfig, 'data'> &
      MockOptions<z.input<TSchema> extends ResponseBase ? z.input<TSchema> : never>
  ): Promise<z.output<TSchema>> {
    if (config?.mock && ENV_CONFIG.enableMocks) {
      return parseResponse(await handleMock(config), responseSchema, 'mocked PATCH', url);
    }

    const response = await axiosInstance.patch(url, data, config);
    return parseResponse(response.data, responseSchema, 'PATCH', url);
  },

  async delete<TSchema extends ZodType>(
    url: string,
    data?: unknown,
    responseSchema?: TSchema,
    config?: Omit<AxiosRequestConfig, 'data'> &
      MockOptions<z.input<TSchema> extends ResponseBase ? z.input<TSchema> : never>
  ): Promise<z.output<TSchema>> {
    if (config?.mock && ENV_CONFIG.enableMocks) {
      return parseResponse(await handleMock(config), responseSchema, 'mocked DELETE', url);
    }

    const response = await axiosInstance.delete(url, { data, ...(config ?? {}) });
    return parseResponse(response.data, responseSchema, 'DELETE', url);
  },
};
